import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, Input, Output, PLATFORM_ID } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Compressor from 'compressorjs';
import { WebcamImage } from 'ngx-webcam';
import { PDFDocument } from 'pdf-lib';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UploadComponentString } from 'src/app/shared/content/content';
import Swal from 'sweetalert2';
import { FilesService } from '../../services/files.service';
import { UserAgentService } from '../../services/user-agent.service';

@Component({
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss', '../modal/modal.component.scss'],
    standalone: false
})
export class UploadComponent {
	private trigger: Subject<void> = new Subject<void>();
	private webcamImage!: WebcamImage;
	private capturedImageBase64 = '';

	public isMobile: boolean = false;
	public isPending = false;
	public fieldId = Math.floor(Math.random() * Date.now());
	public showSnapshotModal = false;
	public success: boolean | undefined;
	public error = '';
	public webcamWidth = 600;
	public webcamHeight = 500;

	// Vista previa
	public previewUrl: string | null = null;
	public sanitizedPreviewUrl: SafeResourceUrl | null = null;
	public uploadedFile: File | null = null;
	public showPreviewModal = false;

	@Input() public documentType: any;
	@Input() public checklist: boolean = false;
	@Input() public responsive: boolean = false;
	@Input() public fileName: any;
	@Input() public isPendence: boolean = false;
	@Input() public preview: boolean = false;
	@Input() public userId?: string;
	@Input() public clientType: string = 'PF';
	@Input() public acceptAllFiles: boolean = false;
	@Output() public selectedFile = new EventEmitter<any>();
	@Output() public successUpload = new EventEmitter<boolean>();

	uploadComponentString = UploadComponentString;

	constructor(
		@Inject(PLATFORM_ID) private platformId: Object,
		private filesService: FilesService,
		private userAgent: UserAgentService,
		private ref: ChangeDetectorRef,
		private sanitizer: DomSanitizer
	) {
		this.isMobile = userAgent.verifyMobile();
	}

	ngOnInit(): void {
		this.convertUploadLabelName();
	}

	private convertUploadLabelName(): void {
		if (this.documentType.label === 'CNH') this.documentType.label = 'Enviar foto da CNH';

		if (this.documentType.label === 'Selfie') this.documentType.label = 'Enviar selfie';

		if (this.documentType.label === 'Requerimento empresário') this.documentType.label = 'Contrato Social+ Última  alteração contratual';
	}

	private handleWebcamSnapshotSize(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.webcamWidth = 600;
			this.webcamHeight = 500;

			if (window.innerWidth <= 650) {
				this.webcamWidth = 500;
			}

			if (window.innerWidth <= 550) {
				this.webcamWidth = 400;
			}

			if (window.innerWidth <= 450) {
				this.webcamWidth = 300;
				this.webcamHeight = 400;
			}

			if (window.innerWidth <= 350) {
				this.webcamWidth = 250;
			}
		}
	}

	private dataURItoBlob(dataURI: any): Blob | undefined {
		if (isPlatformBrowser(this.platformId)) {
			const byteString = window.atob(dataURI);
			const arrayBuffer = new ArrayBuffer(byteString.length);
			const int8Array = new Uint8Array(arrayBuffer);
			for (let i = 0; i < byteString.length; i++) {
				int8Array[i] = byteString.charCodeAt(i);
			}
			const blob = new Blob([int8Array], { type: 'image/jpeg' });
			return blob;
		}

		return;
	}

	private compressIfImage(documentType: any, file: any): void {
		const rawCompressedFile: BehaviorSubject<any> = new BehaviorSubject<any>(null);
		const compressingError: BehaviorSubject<any> = new BehaviorSubject<any>(null);

		rawCompressedFile.subscribe({
			next: rawCompressedFile => {
				if (rawCompressedFile) {
					const compressedFile = new File([rawCompressedFile], rawCompressedFile.name, {
						type: rawCompressedFile.type,
					});

					this.uploadDocument(documentType, compressedFile);
				}
			},
			error: (err: any) => {
				this.success = false;
				this.isPending = false;
				this.error = err;
				this.detectChanges();
			},
		});

		compressingError.subscribe({
			next: (errorMessage: any) => {
				if (errorMessage) {
					this.success = false;
					this.isPending = false;
					this.error = errorMessage;
					this.detectChanges();
				}
			},
		});

		new Compressor(file, {
			quality: 0.6,
			convertTypes: ['image/png', 'image/webp', 'image/tiff'],
			convertSize: 0,

			success(result) {
				rawCompressedFile.next(result);
			},
			error(err) {
				compressingError.next(err.message);
			},
		});
	}

	private compressIfPdf(documentType: any, pdfFile: any): void {
		let filePdfBytes: any;

		pdfFile.arrayBuffer().then(async (result: any) => {
			filePdfBytes = result;

			const formattedFilePdf = new File([filePdfBytes], pdfFile.name, { type: pdfFile.type });

			const newPdf = await PDFDocument.load(filePdfBytes);

			this.uploadDocument(documentType, formattedFilePdf);
		});
	}

	private uploadDocument(documentType: any, compressedFile: any): void {
		if (this.checklist) {
			this.selectedFile.emit(compressedFile);

			setTimeout(() => {
				this.success = true;
				this.isPending = false;
				this.closeModal();
			}, 3000);

			return;
		} else {
			this.filesService.uploadDocument(documentType, compressedFile, this.documentType?.documentId, this.userId).subscribe({
				next: (response) => {
					this.success = true;
					this.isPending = false;
					this.fileName = response.data.fileName;
					this.closeModal();
					this.successUpload.emit(true);

					if (this.isPendence) {
						this.selectedFile.emit(compressedFile);
					}

					this.detectChanges();
				},
				error: (err: any) => {
					this.success = false;
					this.isPending = false;
					this.error = err;

					this.detectChanges();
				},
			});
		}
	}

	private detectChanges(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.ref.detectChanges();
		}
	}

	public compressDocument(documentType: any, file: any): void {
		this.fileName = undefined;
		this.success = undefined;
		this.isPending = true;

		if (file.files) {
			file = file.files[0];
		}

		// Generar vista previa
		this.generatePreview(file);

		if (this.clientType === 'PJ' && !file.type.includes('pdf') && !file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png') && !this.acceptAllFiles) {
			Swal.fire({
				title: '¡Atención!',
				text: `Sólo se aceptan archivos en formato PDF o Imagens (.JPG, .JPEG, .PNG).`,
				icon: 'warning',
				confirmButtonText: 'Ok',
				buttonsStyling: false,
				customClass: {
					confirmButton: 'btn btn-primary',
				},
			});
			this.isPending = false;
			return;
		}

		if (file.type.includes('pdf')) {
			this.compressIfPdf(documentType, file);
		} else {
			this.compressIfImage(documentType, file);
		}
	}

	private generatePreview(file: File): void {
		// Limpiar vista previa anterior
		if (this.previewUrl) {
			URL.revokeObjectURL(this.previewUrl);
		}

		// Asegurar que el tipo MIME esté correctamente establecido
		let fileType = file.type;
		if (!fileType || fileType === 'application/octet-stream') {
			fileType = this.getFileTypeFromName(file.name);
		}

		// Crear un nuevo File con el tipo correcto si es necesario
		let fileToPreview = file;
		if (fileType !== file.type) {
			fileToPreview = new File([file], file.name, { type: fileType });
		}

		if (fileType.startsWith('image/') || fileType === 'application/pdf') {
			this.previewUrl = URL.createObjectURL(fileToPreview);
			// Sanitizar la URL para PDFs
			if (fileType === 'application/pdf') {
				this.sanitizedPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl);
			} else {
				this.sanitizedPreviewUrl = null;
			}
		} else {
			this.previewUrl = null;
			this.sanitizedPreviewUrl = null;
		}
		this.uploadedFile = fileToPreview;
		this.detectChanges();
	}

	private getFileTypeFromName(fileName: string): string {
		const extension = fileName.split('.').pop()?.toLowerCase();
		switch (extension) {
			case 'pdf':
				return 'application/pdf';
			case 'jpg':
			case 'jpeg':
				return 'image/jpeg';
			case 'png':
				return 'image/png';
			default:
				return 'application/octet-stream';
		}
	}

	public triggerSnapshot(): void {
		this.trigger.next();
	}

	public handleImage(documentType: any, webcamImage: WebcamImage): void {
		this.webcamImage = webcamImage;
		this.capturedImageBase64 = webcamImage!.imageAsBase64;

		const formattedImageBlob = this.dataURItoBlob(this.capturedImageBase64);
		// @ts-ignore
		const formattedImageFile = new File([formattedImageBlob], 'my-photo.jpg', { type: formattedImageBlob.type });

		// compressDocument ya genera el preview
		this.compressDocument(documentType, formattedImageFile);
	}

	public get triggerObservable(): Observable<any> {
		return this.trigger.asObservable();
	}

	public closeModal(): void {
		this.showSnapshotModal = false;
	}

	public openPreviewModal(): void {
		if (this.previewUrl && this.uploadedFile) {
			// Asegurar que la URL esté sanitizada para PDFs
			if (this.uploadedFile.type === 'application/pdf' || (this.fileName && this.fileName.toLowerCase().endsWith('.pdf'))) {
				if (!this.sanitizedPreviewUrl) {
					this.sanitizedPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl);
				}
			}
			this.showPreviewModal = true;
			this.detectChanges();
		}
	}

	public closePreviewModal(): void {
		this.showPreviewModal = false;
		this.detectChanges();
	}

	@HostListener('window:resize')
	public onResize(): void {
		this.isMobile = this.userAgent.verifyMobile();
		this.handleWebcamSnapshotSize();
	}
}
